import React from "react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
// `Calendar` adı lucide İKONU için kullanılıyor; takvim bileşeni CalendarView.
import { Calendar as CalendarView } from "@/components/ui/calendar";

/**
 * İzin/rapor tarih aralığı — tıklayınca takvimde gösteren açılır kutu.
 *
 * Kartta yalnız "16.09.2026 - 22.09.2026" yazıyordu; yönetici "bu hangi güne
 * denk geliyor, hafta sonuna mı geliyor, işe ne zaman başlıyor" sorularını
 * takvim açıp saymadan yanıtlayamıyordu. İzin kartındaki "İşe başlama" çipi
 * bu deseni zaten kullanıyor; burada aynı desen tüm aralığa uygulanır.
 *
 * SALT OKUNUR: `onSelect` verilmez, yani güne tıklamak hiçbir tarihi
 * değiştirmez — yalnız takvimde yerini gösterir.
 */

const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtShort = (d) => d.toLocaleDateString("tr-TR");
const fmtLong = (d) =>
  d.toLocaleDateString("tr-TR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

function Satir({ etiket, tarih, vurgu }) {
  if (!tarih) return null;
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-xs text-gray-500">{etiket}</span>
      <span className={`text-sm ${vurgu ? "font-semibold text-emerald-700" : "font-medium text-gray-900"}`}>
        {fmtLong(tarih)}
      </span>
    </div>
  );
}

export default function LeaveDateRangePopover({
  startDate,
  endDate,
  returnDate,
  totalDays,
  weekdays,
  weekends,
  className = "",
}) {
  const from = toDate(startDate);
  const to = toDate(endDate);
  const ret = toDate(returnDate);

  const metin = from && to ? `${fmtShort(from)} - ${fmtShort(to)}` : "-";

  // Tarih çözülemiyorsa açılır kutu anlamsız; eski düz metne düş.
  if (!from || !to) {
    return <span className={`text-xs text-gray-600 ${className}`}>🗓 {metin}</span>;
  }

  // İzin iki aya yayılıyorsa tek ay yetmez (ör. 18 Eylül → 1 Ekim).
  const cokAy = from.getMonth() !== to.getMonth() || from.getFullYear() !== to.getFullYear();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Takvimde göster"
          className={`inline-flex items-center gap-1 rounded border border-transparent px-1 text-xs text-gray-600 hover:border-gray-300 hover:bg-white hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${className}`}
        >
          🗓 {metin}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="space-y-1 border-b px-3 py-2">
          <Satir etiket="Başlangıç" tarih={from} />
          <Satir etiket="Bitiş" tarih={to} />
          <Satir etiket="İşe başlama" tarih={ret} vurgu />
          {(totalDays != null || weekdays != null || weekends != null) && (
            <p className="pt-1 text-[11px] text-gray-500">
              {totalDays != null && `Toplam ${totalDays} gün`}
              {weekdays != null && ` · ${weekdays} hafta içi`}
              {weekends != null && ` · ${weekends} hafta sonu`}
            </p>
          )}
        </div>
        <CalendarView
          mode="range"
          selected={{ from, to }}
          defaultMonth={from}
          numberOfMonths={cokAy ? 2 : 1}
          // İşe başlama günü aralığın DIŞINDA; ayrı bir işaretle gösterilir ki
          // "izin bitişi" ile "işe dönüş" karışmasın.
          modifiers={ret ? { iseBaslama: ret } : undefined}
          modifiersClassNames={{
            iseBaslama: "font-bold text-emerald-700 ring-2 ring-emerald-500 rounded-md",
          }}
        />
        <p className="border-t px-3 py-1.5 text-[11px] text-gray-400">
          Boyalı günler izin aralığı · yeşil halka işe başlama günü
        </p>
      </PopoverContent>
    </Popover>
  );
}
